/**
 * Call backup skill for Patrol enemies — spikes aggro on nearby enemies via callback.
 * Windup (calling signal) → active (fires alert callback once) → recovery → cooldown.
 * The actual aggro spike is an aggro spike, not a spawn — nearby idle/alert enemies snap to chase.
 */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';

export interface EnemyBackupConfig {
  backupRadius: number;
  windupMs: number;
  recoveryMs: number;
  cooldownMs: number;
}

/** Callback signature for alerting nearby enemies — called once on transition to active phase. */
export type BackupAlertCallback = (originX: number, originY: number, radius: number) => void;

/** Calls nearby allies to converge on the caster's position within a configurable radius. */
export class EnemyBackupSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'active', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyBackupConfig;
  private _alertCallback: BackupAlertCallback | null = null;

  /** Patrol position — set before activation. */
  private _originX: number = 0;
  private _originY: number = 0;

  constructor(skillId: string, config: EnemyBackupConfig) {
    super();
    this.skillId = skillId;
    this.config = config;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 50; // Brief — just triggers the callback
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this.fireAlert();
    }
  }

  /** Set the callback that fires when backup is triggered. */
  setAlertCallback(callback: BackupAlertCallback): void {
    this._alertCallback = callback;
  }

  /** Set the Patrol's current position. */
  setOrigin(x: number, y: number): void {
    this._originX = x;
    this._originY = y;
  }

  isInDamagePhase(): boolean { return false; }

  getMovement(): EnemySkillMovement | null {
    if (this.phase === 'windup' || this.phase === 'active' || this.phase === 'recovery') {
      return { vx: 0, vy: 0 };
    }
    return null;
  }

  getDamageInfo(): EnemySkillDamageInfo | null { return null; }

  destroy(): void {
    super.destroy();
    this._alertCallback = null;
  }

  private fireAlert(): void {
    if (this._alertCallback) {
      this._alertCallback(this._originX, this._originY, this.config.backupRadius);
    }
  }
}
